import React, { useCallback, useEffect, useRef, useState } from 'react';
import Tooltip from '../tooltip';
import './index.less';
import { useLocaleStore } from '../i18n';

interface ISelectAngleProps {
    /**
     * 角度
     */
    angle: number;

    /**
     * 提示文案
     */
    title?: string;

    /**
     * 是否展示提示
     */
    tooltipVisible?: boolean;

    /**
     * 角度实时变化的回调
     */
    onChange?: (angle: number) => void;

    /**
     * 放开旋转角度后的回调
     */
    onAfterChange?: (angle: number) => void;

    /**
     * 是否显示角度值
     */
    showValue?: boolean;
}

type AngleMode = '15deg' | '1deg';

const SelectAngle: React.FC<ISelectAngleProps> = (props) => {
    const { $t } = useLocaleStore();
    const {
        angle,
        title = $t((v) => v.selectAngle.title),
        tooltipVisible,
        showValue = true,
        onChange,
        onAfterChange,
    } = props;
    const [mousedown, setMousedown] = useState(false);
    const [currentAngle, setCurrentAngle] = useState(angle);
    const ref = useRef<HTMLDivElement>(null);
    /** 鼠标滑动默认按1度吸附, shift加鼠标滑动才为15度吸附 */
    const angleModeRef = useRef<AngleMode>('1deg');

    useEffect(() => {
        const handleKeydown = (e: KeyboardEvent) => {
            if (e.shiftKey) {
                angleModeRef.current = '15deg';
            }
        };
        const handleKeyup = () => {
            angleModeRef.current = '1deg';
        };
        window.addEventListener('keydown', handleKeydown, { capture: true });
        window.addEventListener('keyup', handleKeyup, { capture: true });

        return () => {
            window.removeEventListener('keydown', handleKeydown, { capture: true });
            window.removeEventListener('keyup', handleKeyup, { capture: true });
        };
    }, []);

    useEffect(() => {
        setCurrentAngle(angle);
    }, [angle]);

    const computeAngle = (event: React.MouseEvent, angleMode: AngleMode) => {
        if (!ref.current) return;

        // 旋转角度模块的中心点坐标
        const centerX = ~~ref.current.offsetHeight / 2 + ref.current.getBoundingClientRect().left;
        const centerY = ~~ref.current.offsetWidth / 2 + ref.current.getBoundingClientRect().top;

        // 鼠标坐标
        const mx = event.pageX || event.clientX + document.body.scrollLeft;
        const my = event.pageY || event.clientY + document.body.scrollTop;

        const angle = Math.round(calculateDegree(mx, my, centerX, centerY));

        let resultAngle = angle;
        if (angleMode === '15deg') {
            resultAngle = Math.floor(angle / 15) * 15 + (angle % 15 > 7 ? 15 : 0);
        }

        return resultAngle;
    };

    const onMouseDown = (event: React.MouseEvent) => {
        if (!ref.current) {
            return;
        }
        setMousedown(true);
        const angleTemp = computeAngle(event, angleModeRef.current);
        if (angleTemp) {
            setCurrentAngle(angleTemp);
            handleChange(~~angleTemp);
        }
    };

    const handleMouseMove = useCallback(
        (event: MouseEvent) => {
            if (!mousedown || !ref.current) {
                return;
            }
            const angleTemp = computeAngle(event as unknown as React.MouseEvent, angleModeRef.current);
            if (angleTemp) {
                setCurrentAngle(angleTemp);
                handleChange(~~angleTemp);
            }
        },
        [mousedown],
    );

    const handleMouseUp = useCallback(() => {
        if (!mousedown) {
            return;
        }

        setMousedown(false);
        onAfterChange?.(angle);
    }, [mousedown, angle]);

    const handleChange = (angle: number) => {
        onChange?.(angle);
    };

    // 角度计算公式
    const calculateDegree = (x: number, y: number, centerX: number, centerY: number) => {
        const radians = Math.atan2(centerY - y, x - centerX);
        return radians * (180 / Math.PI);
    };

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }, [handleMouseMove]);

    useEffect(() => {
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseUp]);

    return (
        <Tooltip title={title} open={tooltipVisible}>
            <div className="mt-select-angle">
                <div ref={ref} className="mt-select-angle__operate" onMouseDown={onMouseDown}>
                    <div
                        className="mt-select-angle__container"
                        style={{ transform: 'rotate(' + (-currentAngle + 90 + 'deg)') }}
                    >
                        <div className="mt-select-angle__point" />
                    </div>
                </div>
                {showValue ? <div className="mt-select-angle__text">{~~currentAngle}°</div> : null}
            </div>
        </Tooltip>
    );
};

export default SelectAngle;
